/**
 * React Hook for SignalR Connection
 * Manages SignalR connection lifecycle and handles real-time notifications
 */

import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as signalR from "@microsoft/signalr";
import { signalRService } from "../services/signalr.service";
import { authService } from "../services/auth.service";
import { toast } from "sonner";
import type { OrderCreatedNotification, ReceiptCreatedNotification } from "../types";

export const useSignalR = () => {
  const queryClient = useQueryClient();
  const firmIdRef = useRef<number | null>(null);
  const orderHandlerRef = useRef<((data: OrderCreatedNotification) => void) | null>(null);
  const receiptHandlerRef = useRef<((data: ReceiptCreatedNotification) => void) | null>(null);
  const stateChangeHandlerRef = useRef<((state: signalR.HubConnectionState) => void) | null>(null);

  /**
   * Handle OrderCreated event
   */
  const handleOrderCreated = useCallback((data: OrderCreatedNotification) => {
    // Only process notifications for the current user's firm
    if (firmIdRef.current && data.firmId === firmIdRef.current) {
      // Invalidate notification count query to refresh the count
      queryClient.invalidateQueries({ queryKey: ["notification", "count", data.firmId] });

      // Show toast notification
      toast.success("New Order Created", {
        description: `Order ${data.orderNo}: ${data.message} (${data.insertedCount} items)`,
        duration: 5000,
      });
    }
  }, [queryClient]);

  /**
   * Handle ReceiptCreated event
   */
  const handleReceiptCreated = useCallback((data: ReceiptCreatedNotification) => {
    // Only process notifications for the current user's firm
    if (firmIdRef.current && data.firmId === firmIdRef.current) {
      // Invalidate notification count query to refresh the count
      queryClient.invalidateQueries({ queryKey: ["notification", "count", data.firmId] });

      // Show toast notification
      toast.success("New Receipt Created", {
        description: `Receipt ${data.receiptNo}: ${data.message}`,
        duration: 5000,
      });
    }
  }, [queryClient]);

  /**
   * Initialize SignalR connection and join firm group
   */
  const initializeConnection = useCallback(async () => {
    const user = authService.getCurrentUser();
    if (!user || !user.firmId) {
      return;
    }

    const firmId = user.firmId;
    firmIdRef.current = firmId;

    try {
      // Start SignalR connection
      await signalRService.start();

      // Join firm group
      await signalRService.joinFirmGroup(firmId);

      // Register event handlers
      orderHandlerRef.current = handleOrderCreated;
      receiptHandlerRef.current = handleReceiptCreated;

      signalRService.onOrderCreated(handleOrderCreated);
      signalRService.onReceiptCreated(handleReceiptCreated);

      if (!import.meta.env.PROD) {
        console.log(`SignalR initialized for firm ${firmId}`);
      }
    } catch (error) {
      console.error("Failed to initialize SignalR connection:", error);
      // Don't show error toast to avoid spamming user
      // Connection will retry automatically
    }
  }, [handleOrderCreated, handleReceiptCreated]);

  /**
   * Cleanup SignalR connection
   */
  const cleanupConnection = useCallback(async () => {
    if (firmIdRef.current) {
      try {
        // Remove event handlers
        if (orderHandlerRef.current) {
          signalRService.offOrderCreated(orderHandlerRef.current);
        }
        if (receiptHandlerRef.current) {
          signalRService.offReceiptCreated(receiptHandlerRef.current);
        }

        // Leave firm group
        await signalRService.leaveFirmGroup(firmIdRef.current);

        // Stop connection
        await signalRService.stop();

        firmIdRef.current = null;
        orderHandlerRef.current = null;
        receiptHandlerRef.current = null;

        if (!import.meta.env.PROD) {
          console.log("SignalR connection cleaned up");
        }
      } catch (error) {
        console.error("Error cleaning up SignalR connection:", error);
      }
    }
  }, []);

  /**
   * Handle reconnection - rejoin firm group when reconnected
   */
  const handleReconnection = useCallback(async () => {
    if (firmIdRef.current && signalRService.isConnected()) {
      try {
        await signalRService.joinFirmGroup(firmIdRef.current);
        if (!import.meta.env.PROD) {
          console.log(`Rejoined firm group after reconnection: firm-${firmIdRef.current}`);
        }
      } catch (error) {
        console.error("Error rejoining firm group after reconnection:", error);
      }
    }
  }, []);

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = authService.isAuthenticated();
    if (!isAuthenticated) {
      return;
    }

    // Initialize connection
    initializeConnection();

    // Register reconnection handler (only once)
    if (!stateChangeHandlerRef.current) {
      stateChangeHandlerRef.current = (state: signalR.HubConnectionState) => {
        if (state === signalR.HubConnectionState.Connected && firmIdRef.current) {
          // Rejoin group when reconnected
          handleReconnection();
        }
      };
      signalRService.onStateChange(stateChangeHandlerRef.current);
    }

    // Cleanup on unmount
    return () => {
      cleanupConnection();
      stateChangeHandlerRef.current = null;
    };
  }, [initializeConnection, cleanupConnection, handleReconnection]);

  // Handle cross-tab authentication state changes (for multi-tab scenarios)
  useEffect(() => {
    // Listen for storage changes (token/user changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "auth_token" || e.key === "user") {
        const isAuthenticated = authService.isAuthenticated();
        if (isAuthenticated && !firmIdRef.current) {
          // User logged in (in another tab), initialize connection
          initializeConnection();
        } else if (!isAuthenticated && firmIdRef.current) {
          // User logged out (in another tab), cleanup connection
          cleanupConnection();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [initializeConnection, cleanupConnection]);
};
